import { useReducer, useCallback } from "react";
import { message } from "antd";
import LocationService from "../services/Locations";
import BathAddonLocationsService from "../services/BathAddonLocations";

const BATH_KEYWORD = "bath";
const BOARDING_KEYWORD = "boarding";

const initialState = {
	visible: false,
	location: null,
	config: null,
	nightThreshold: null,
	bathServiceId: null,
	saving: false,
	services: [],
	loading: false,
};

function reducer(state, action) {
	switch (action.type) {
		case "OPEN_MODAL":
			return {
				...initialState,
				visible: true,
				location: action.payload.location,
				config: action.payload.config,
				nightThreshold: action.payload.config?.nightThreshold ?? null,
				bathServiceId: action.payload.config?.bathServiceId ?? null,
				loading: true,
			};
		case "SET_SERVICES":
			return { ...state, services: action.payload, loading: false };
		case "SET_SAVING":
			return { ...state, saving: action.payload };
		case "SET_NIGHT_THRESHOLD":
			return { ...state, nightThreshold: action.payload };
		case "SET_BATH_SERVICE_ID":
			return { ...state, bathServiceId: action.payload };
		case "CLOSE_MODAL":
			return { ...initialState };
		default:
			return state;
	}
}

export function useBathAddonModal(getLocations) {
	const [state, dispatch] = useReducer(reducer, initialState);

	const openModal = useCallback(async (location) => {
		dispatch({
			type: "OPEN_MODAL",
			payload: { location, config: location.bathAddonLocation },
		});

		try {
			const types = await LocationService.getLocationReservationTypes({
				locationId: location.id,
			});
			const boardingType = (types ?? []).find((t) =>
				t.name.toLowerCase().includes(BOARDING_KEYWORD) &&
				t.booking_category_id === 1,
			);

			if (!boardingType) {
				message.error("No boarding reservation type found for this location");
				dispatch({ type: "SET_SERVICES", payload: [] });
				return;
			}

			const result = await LocationService.getReservationTypeServices({
				locationId: location.id,
				reservationTypeId: boardingType.id,
			});

			const bathServices = (result?.allowable_services ?? []).filter((svc) =>
				svc.name.toLowerCase().includes(BATH_KEYWORD),
			);

			if (bathServices.length === 0) {
				message.info(
					"No bath services found for the boarding reservation type",
				);
			}

			dispatch({ type: "SET_SERVICES", payload: bathServices });
		} catch {
			dispatch({ type: "SET_SERVICES", payload: [] });
		}
	}, []);

	const closeModal = useCallback(() => {
		dispatch({ type: "CLOSE_MODAL" });
	}, []);

	const save = async () => {
		if (!state.nightThreshold || state.nightThreshold < 1) {
			message.error("Night threshold must be at least 1");
			return;
		}
		if (!state.bathServiceId) {
			message.error("Bath service is required");
			return;
		}

		dispatch({ type: "SET_SAVING", payload: true });

		try {
			if (state.config) {
				await BathAddonLocationsService.update(state.config.id, {
					nightThreshold: state.nightThreshold,
					bathServiceId: state.bathServiceId,
				});
				message.success("Bath add-on location updated");
			} else {
				await BathAddonLocationsService.create({
					locId: state.location.id,
					nightThreshold: state.nightThreshold,
					bathServiceId: state.bathServiceId,
				});
				message.success("Bath add-on location created");
			}

			dispatch({ type: "CLOSE_MODAL" });
			getLocations();
		} catch (error) {
			message.error(error?.data?.message || "An error occurred");
		} finally {
			dispatch({ type: "SET_SAVING", payload: false });
		}
	};

	const remove = async () => {
		dispatch({ type: "SET_SAVING", payload: true });

		try {
			await BathAddonLocationsService.delete(state.config.id);
			message.success("Bath add-on location deleted");
			dispatch({ type: "CLOSE_MODAL" });
			getLocations();
		} catch (error) {
			message.error(error?.data?.message || "An error occurred");
		} finally {
			dispatch({ type: "SET_SAVING", payload: false });
		}
	};

	const setNightThreshold = useCallback((val) => {
		dispatch({ type: "SET_NIGHT_THRESHOLD", payload: val });
	}, []);

	const setBathServiceId = useCallback((val) => {
		dispatch({ type: "SET_BATH_SERVICE_ID", payload: val });
	}, []);

	return {
		state,
		openModal,
		closeModal,
		save,
		remove,
		setNightThreshold,
		setBathServiceId,
	};
}
